import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';

import { DISPATCHER_ORDER_UPDATED_EVENT } from '../dispatcher_constants';
import {
  applyOrderDrop,
  cloneDispatcherData,
  computeDragDispatchOffset,
  createEmptyDispatch,
  createThrottledDragMove,
  defaultDropZoneStyle,
  dispatchLayoutRefresh,
  DispatcherDropZoneStyle,
  DispatcherRegionConfig,
  highlightDropZoneStyle,
  resolveEditorDispatch,
  MISSING_ESTIMATED_DURATION_MESSAGE,
} from '../dispatcher_editor_utils';
import { buildDispatcherLayout } from '../dispatcher_layout';
import { DispatcherData, DispatcherDispatchRecord, DispatcherOrderRecord } from '../dispatcher_types';
import { useDispatcherError } from './use_dispatcher_error';
import { DispatcherResources } from './use_dispatcher_resources';

type UseDispatcherEditorOptions = {
  data: DispatcherData | undefined;
  resources: DispatcherResources | undefined;
  regionId: number | undefined;
  regionTz: string | undefined;
  modifying: boolean;
  routeLockActive?: boolean;
};

const scheduleLayoutRefresh = () => {
  window.queueMicrotask(() => dispatchLayoutRefresh());
};

export const useDispatcherEditor = ({
  data,
  resources,
  regionId,
  regionTz,
  modifying,
  routeLockActive = true,
}: UseDispatcherEditorOptions) => {
  const { showError, confirmation } = useDispatcherError();
  const [editorData, setEditorData] = useState<DispatcherData | null>(null);
  const editorDataRef = useRef<DispatcherData | null>(null);
  const [regionConfig, setRegionConfig] = useState<DispatcherRegionConfig | null>(null);
  const draggingRef = useRef<DispatcherOrderRecord | null>(null);
  const dropHandledRef = useRef(false);
  const draggingOffsetRef = useRef(0);
  const [defaultDropZone, setDefaultDropZone] = useState<DispatcherDropZoneStyle | null>(null);
  const [highlightDropZone, setHighlightDropZone] = useState<DispatcherDropZoneStyle | null>(null);

  useEffect(() => {
    editorDataRef.current = editorData;
  }, [editorData]);

  useEffect(() => {
    if (!regionId || !regionTz) {
      setRegionConfig(null);
      return;
    }

    let cancelled = false;
    axios
      .get<DispatcherRegionConfig[]>('/regions.json')
      .then((response) => {
        const region = response.data.find((entry) => Number(entry.id) === regionId);
        if (!cancelled && region) {
          setRegionConfig({ ...region, id: regionId, tz: regionTz });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRegionConfig(null);
          showError('Unable to load region settings. Please try again.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [regionId, regionTz, showError]);

  useEffect(() => {
    if (data && modifying && !editorData) {
      setEditorData(cloneDispatcherData(data));
    }
  }, [data, editorData, modifying]);

  useEffect(() => {
    const handleOrderUpdated = () => {
      setEditorData((current) => (current ? { ...current, orders: [...current.orders] } : current));
      scheduleLayoutRefresh();
    };

    document.addEventListener(DISPATCHER_ORDER_UPDATED_EVENT, handleOrderUpdated);
    return () => document.removeEventListener(DISPATCHER_ORDER_UPDATED_EVENT, handleOrderUpdated);
  }, []);

  const activeData = modifying && editorData ? editorData : data;
  const layout = useMemo(() => (activeData ? buildDispatcherLayout(activeData) : undefined), [activeData]);

  const resetEditor = useCallback(() => {
    setEditorData(null);
    draggingRef.current = null;
    draggingOffsetRef.current = 0;
    setDefaultDropZone(null);
    setHighlightDropZone(null);
  }, []);

  const beginEditing = useCallback(() => {
    if (data) {
      setEditorData(cloneDispatcherData(data));
    }
  }, [data]);

  const removeDispatch = useCallback((dispatch: DispatcherDispatchRecord) => {
    setEditorData((current) => {
      if (!current) {
        return current;
      }

      const target = resolveEditorDispatch(current.dispatches, dispatch);
      if (!target) {
        return current;
      }

      const dispatches = target.id
        ? current.dispatches.map((entry) => (entry === target ? { ...entry, _destroy: true } : entry))
        : current.dispatches.filter((entry) => entry !== target);

      return { ...current, dispatches };
    });
    scheduleLayoutRefresh();
  }, []);

  const addDispatch = useCallback(
    (shouldRefreshLayout = true): DispatcherDispatchRecord | null => {
      if (!resources || !regionConfig) {
        showError('Unable to add a route until region settings finish loading.');
        return null;
      }

      const dispatch = createEmptyDispatch({
        region: regionConfig,
        resources,
        dragging: draggingRef.current,
      });

      if (!dispatch) {
        if (draggingRef.current && !draggingRef.current.estimated_duration) {
          showError(MISSING_ESTIMATED_DURATION_MESSAGE);
        }
        return null;
      }

      setEditorData((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          dispatches: [...current.dispatches, dispatch],
        };
      });

      if (shouldRefreshLayout) {
        scheduleLayoutRefresh();
      }

      return dispatch;
    },
    [regionConfig, resources, showError],
  );

  const handleDrop = useCallback(
    (selection: DispatcherDispatchRecord | null) => {
      dropHandledRef.current = true;
      const dragging = draggingRef.current;

      setDefaultDropZone(null);
      setHighlightDropZone(null);

      if (!dragging) {
        return;
      }

      const current = editorDataRef.current;
      if (!current) {
        draggingRef.current = null;
        return;
      }

      const resolvedSelection = selection ? (resolveEditorDispatch(current.dispatches, selection) ?? selection) : null;
      const result = applyOrderDrop({
        editorData: current,
        selection: resolvedSelection,
        dragging,
        draggingDispatchOffset: draggingOffsetRef.current,
        routeLockActive,
        manageMode: modifying,
      });

      draggingRef.current = null;

      if (result.error) {
        showError(result.error);
        return;
      }

      if (result.data) {
        setEditorData(result.data);
      }

      scheduleLayoutRefresh();
    },
    [modifying, routeLockActive, showError],
  );

  const handleDropOnAddRoute = useCallback(() => {
    dropHandledRef.current = true;
    const dragging = draggingRef.current;

    setDefaultDropZone(null);
    setHighlightDropZone(null);

    if (!resources || !regionConfig) {
      showError('Unable to add a route until region settings finish loading.');
      draggingRef.current = null;
      return;
    }

    const current = editorDataRef.current;
    if (!current) {
      draggingRef.current = null;
      return;
    }

    const dispatch = createEmptyDispatch({
      region: regionConfig,
      resources,
      dragging,
    });

    if (!dispatch) {
      if (dragging && !dragging.estimated_duration && dragging.type !== 'restock' && dragging.type !== 'disposal') {
        showError(MISSING_ESTIMATED_DURATION_MESSAGE);
      }
      draggingRef.current = null;
      return;
    }

    const dataWithDispatch: DispatcherData = {
      ...current,
      dispatches: [...current.dispatches, dispatch],
    };

    let nextData: DispatcherData = dataWithDispatch;

    if (dragging) {
      const result = applyOrderDrop({
        editorData: dataWithDispatch,
        selection: dispatch,
        dragging,
        draggingDispatchOffset: draggingOffsetRef.current,
        routeLockActive,
        manageMode: modifying,
      });

      if (result.error) {
        showError(result.error);
        draggingRef.current = null;
        return;
      }

      if (result.data) {
        nextData = result.data;
      }
    }

    draggingRef.current = null;
    setEditorData(nextData);
    scheduleLayoutRefresh();
  }, [modifying, regionConfig, resources, routeLockActive, showError]);

  const handleDragMove = useMemo(
    () =>
      createThrottledDragMove((order: DispatcherOrderRecord, event: DragEvent) => {
        if (!draggingRef.current) {
          return;
        }

        const mainElement = document.querySelector<HTMLElement>('.dispatcher-main');
        const dropzoneElement = document.querySelector<HTMLElement>('.dispatcher-dropzone');
        if (!mainElement || !dropzoneElement) {
          return;
        }

        draggingOffsetRef.current = computeDragDispatchOffset(order, event.clientY, mainElement, dropzoneElement);
        setHighlightDropZone(highlightDropZoneStyle(order, draggingOffsetRef.current));
      }),
    [],
  );

  const handleDragStart = useCallback((order: DispatcherOrderRecord) => {
    dropHandledRef.current = false;
    draggingOffsetRef.current = order.dispatch_offset ?? 0;
    draggingRef.current = order;
    setDefaultDropZone(defaultDropZoneStyle(order, 'block'));
    setHighlightDropZone(highlightDropZoneStyle(order, draggingOffsetRef.current));
  }, []);

  const handleDragEnd = useCallback((order: DispatcherOrderRecord) => {
    setDefaultDropZone(defaultDropZoneStyle(order, 'none'));
    setHighlightDropZone(null);
    window.setTimeout(() => {
      if (!dropHandledRef.current) {
        draggingRef.current = null;
      }
      dropHandledRef.current = false;
    }, 0);
  }, []);

  return {
    layout,
    activeData,
    beginEditing,
    resetEditor,
    removeDispatch,
    addDispatch,
    handleDrop,
    handleDropOnAddRoute,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    defaultDropZone,
    highlightDropZone,
    confirmation,
  };
};
